import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  inject,
} from "@angular/core";
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { TableFieldTypes } from "../../../../locale/consts.en";
import { TableFieldType } from "../../table-field/table-field.model";
import {
  CHART_AGGREGATIONS,
  CHART_TYPES,
  ChartAggregation,
  ChartType,
  ChartTypeMeta,
  DEFAULT_BAND_COLORS,
  DEFAULT_BAND_EDGES,
  DEFAULT_SERIES_COLORS,
  ServiceChart,
  ServiceChartForm,
  ServiceChartSeriesForm,
  chartTypeMeta,
  dropdownNumericMap,
  isCategoryColumnAllowed,
  isNumericAxisColumn,
  isNumericFieldType,
} from "../chart.model";
import {
  ChartPreviewConfig,
  PreviewColumn,
  PreviewData,
  generatePreviewData,
  renderChartSvg,
} from "../chart-preview";

export interface ChartDesignerDialogData {
  tableName: string;
  columns: PreviewColumn[];
  chart?: Partial<ServiceChart>;
  /** render tags already used by other charts in the template — collision guard */
  usedRenderTags?: string[];
}

@Component({
  selector: "app-chart-designer-dialog",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
  templateUrl: "./chart-designer-dialog.component.html",
  styleUrl: "./chart-designer-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ChartDesignerDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<ChartDesignerDialogComponent>>(MatDialogRef);
  private sanitizer = inject(DomSanitizer);

  readonly chartTypes: ChartTypeMeta[] = CHART_TYPES;
  readonly aggregations: ChartAggregation[] = CHART_AGGREGATIONS;
  readonly fieldTypeLabels = TableFieldTypes;

  form: FormGroup;
  columns: PreviewColumn[];

  rowCount = 8;
  valueMin = 0;
  valueMax = 1;

  /** SURFACE_3D preview view controls (not persisted — preview only) */
  viewAzimuth = 45;
  viewElevation = 30;
  surfaceOpacity = 1;
  smoothSurface = true;

  previewSvg: SafeHtml | null = null;
  previewData: PreviewData = null;
  private generatedKey = "";
  errors: string[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChartDesignerDialogData,
  ) {
    this.columns = data.columns ?? [];
    this.form = new FormGroup(
      new ServiceChartForm(data.chart ?? {}) as unknown,
    );
    // view controls are persisted on the chart (used by the backend renderer);
    // seed the live editing state from the saved values when editing
    const c = data.chart;
    if (c) {
      if (c.viewAzimuth != null) {
        this.viewAzimuth = c.viewAzimuth;
      }
      if (c.viewElevation != null) {
        this.viewElevation = c.viewElevation;
      }
      if (c.surfaceOpacity != null) {
        this.surfaceOpacity = c.surfaceOpacity;
      }
      if (c.smoothSurface != null) {
        this.smoothSurface = c.smoothSurface;
      }
    }
  }

  ngOnInit(): void {
    if (this.needsBands && this.bandEdges.length < 2) {
      this.seedBands();
    }
    if (this.series.length === 0) {
      this.addSeries();
    }
    // generation range defaults to 0 – 1.0; banded charts follow their edges
    if (this.needsBands) {
      this.matchValueRangeToBands();
    }
    this.form.valueChanges.subscribe(() => this.recompute());
    this.recompute();
  }

  // ----- typed control accessors -------------------------------------------

  get series(): FormArray<FormGroup> {
    return this.form.get("series") as FormArray<FormGroup>;
  }
  get bandEdges(): FormArray<FormControl> {
    return this.form.get("bandEdges") as FormArray<FormControl>;
  }
  get bandColors(): FormArray<FormControl> {
    return this.form.get("bandColors") as FormArray<FormControl>;
  }
  get chartType(): ChartType {
    return this.form.get("chartType").value as ChartType;
  }
  get meta(): ChartTypeMeta {
    return chartTypeMeta(this.chartType);
  }
  get needsYAxis(): boolean {
    return this.meta.needsYAxis;
  }
  get needsBands(): boolean {
    return this.meta.needsBands;
  }

  // ----- column pick lists (filtered, never free text) ---------------------

  private usedKeys(exceptSeriesIndex?: number): Set<string> {
    const used = new Set<string>();
    const cat = this.form.get("categoryColumnKey").value;
    const y = this.form.get("yAxisColumnKey").value;
    if (cat) {
      used.add(cat);
    }
    if (y && this.needsYAxis) {
      used.add(y);
    }
    this.series.controls.forEach((s, i) => {
      if (i === exceptSeriesIndex) {
        return;
      }
      const v = s.get("valueColumnKey").value;
      if (v) {
        used.add(v);
      }
    });
    return used;
  }

  categoryColumns(): PreviewColumn[] {
    const used = this.usedKeys();
    const current = this.form.get("categoryColumnKey").value;
    return this.columns.filter(
      (c) =>
        isCategoryColumnAllowed(this.meta, c) &&
        (!used.has(c.columnKey) || c.columnKey === current),
    );
  }

  yAxisColumns(): PreviewColumn[] {
    const used = this.usedKeys();
    const current = this.form.get("yAxisColumnKey").value;
    return this.columns.filter(
      (c) =>
        isNumericAxisColumn(c) &&
        (!used.has(c.columnKey) || c.columnKey === current),
    );
  }

  seriesColumns(index: number): PreviewColumn[] {
    const used = this.usedKeys(index);
    const current = this.series.at(index).get("valueColumnKey").value;
    return this.columns.filter(
      (c) =>
        isNumericFieldType(c.type) &&
        (!used.has(c.columnKey) || c.columnKey === current),
    );
  }

  columnLabel(key: string): string {
    return this.columns.find((c) => c.columnKey === key)?.name ?? key;
  }

  columnTypeLabel(type: TableFieldType | number): string {
    return this.fieldTypeLabels[type as TableFieldType] ?? "";
  }

  /** Hint shown next to a column in the reference list for banded chart types. */
  columnAxisNote(c: PreviewColumn): string {
    if (!this.needsYAxis || isNumericFieldType(c.type)) {
      return "";
    }
    if (+c.type === TableFieldType.DROPDOWN) {
      return dropdownNumericMap(c)
        ? $localize`numeric values → usable as X/Y axis`
        : $localize`assign numeric values to options to use as X/Y axis`;
    }
    return $localize`not usable as an axis for this chart type`;
  }

  // ----- chart type / series / bands mutation -----------------------------

  onChartTypeChange(type: ChartType): void {
    this.form.get("chartType").setValue(type);
    const meta = chartTypeMeta(type);

    while (this.series.length > meta.maxSeries) {
      this.series.removeAt(this.series.length - 1);
    }
    if (this.series.length === 0) {
      this.addSeries();
    }

    const catKey = this.form.get("categoryColumnKey").value;
    const catCol = this.columns.find((c) => c.columnKey === catKey);
    if (catCol && !isCategoryColumnAllowed(meta, catCol)) {
      this.form.get("categoryColumnKey").setValue(null);
    }
    const yKey = this.form.get("yAxisColumnKey").value;
    const yCol = this.columns.find((c) => c.columnKey === yKey);
    if (yCol && !isNumericAxisColumn(yCol)) {
      this.form.get("yAxisColumnKey").setValue(null);
    }

    if (meta.needsBands) {
      this.form.get("aggregation").setValue("NONE");
      if (this.bandEdges.length < 2) {
        this.seedBands();
      }
    }
    if (!meta.needsYAxis) {
      this.form.get("yAxisColumnKey").setValue(null);
    }
    this.recompute();
  }

  addSeries(): void {
    if (this.series.length >= this.meta.maxSeries) {
      return;
    }
    const color =
      DEFAULT_SERIES_COLORS[this.series.length % DEFAULT_SERIES_COLORS.length];
    this.series.push(
      new FormGroup(
        new ServiceChartSeriesForm({ color, sort: this.series.length }) as unknown,
      ),
    );
  }

  removeSeries(index: number): void {
    this.series.removeAt(index);
    if (this.series.length === 0) {
      this.addSeries();
    }
  }

  private seedBands(): void {
    this.bandEdges.clear();
    this.bandColors.clear();
    DEFAULT_BAND_EDGES.forEach((e) =>
      this.bandEdges.push(new FormControl(e)),
    );
    DEFAULT_BAND_COLORS.forEach((c) =>
      this.bandColors.push(new FormControl(c)),
    );
  }

  private bandNums(): number[] {
    return (this.bandEdges.value as number[]).map(Number);
  }

  /** smallest step / minimum width between two edges, scaled to the range */
  get bandGap(): number {
    const e = this.bandNums();
    const span = (e[e.length - 1] ?? 1) - (e[0] ?? 0);
    return span > 0 ? Math.max(span / 200, 1e-6) : 0.01;
  }

  get bandStep(): number {
    const g = this.bandGap;
    // a friendly input step: round to 1 significant figure of the gap
    const mag = Math.pow(10, Math.floor(Math.log10(g)));
    return mag;
  }

  private roundEdge(v: number): number {
    return Math.round(v * 1e6) / 1e6;
  }

  edgeValue(index: number): number {
    return Number(this.bandEdges.at(index)?.value ?? 0);
  }

  bandWeight(i: number): number {
    const e = this.bandNums();
    const total = (e[e.length - 1] - e[0]) || 1;
    return Math.max(0.03, (e[i + 1] - e[i]) / total);
  }

  bandRangeText(i: number): string {
    return `${this.fmt(this.edgeValue(i))} – ${this.fmt(this.edgeValue(i + 1))}`;
  }

  /**
   * Set one edge and keep the whole ladder strictly increasing: an edge can't
   * drop to/below the one under it, and raising it drags every edge above up.
   */
  setBandEdge(index: number, raw: string | number): void {
    const edges = this.bandEdges.controls as FormControl[];
    let v = Number(raw);
    if (!Number.isFinite(v)) {
      return;
    }
    const gap = this.bandGap;
    if (index > 0) {
      const prev = Number(edges[index - 1].value);
      if (v <= prev) {
        v = prev + gap;
      }
    }
    edges[index].setValue(this.roundEdge(v));
    // cascade upward
    for (let k = index + 1; k < edges.length; k++) {
      const lower = Number(edges[k - 1].value);
      if (Number(edges[k].value) <= lower) {
        edges[k].setValue(this.roundEdge(lower + gap));
      }
    }
    this.bandEdges.markAsDirty();
  }

  setBandColor(index: number, color: string): void {
    this.bandColors.at(index).setValue(color);
    this.bandColors.markAsDirty();
  }

  addBand(): void {
    const edges = this.bandNums();
    const last = edges[edges.length - 1] ?? 0;
    const prevGap =
      edges.length >= 2 ? last - edges[edges.length - 2] : this.bandGap * 20;
    this.bandEdges.push(
      new FormControl(this.roundEdge(last + (prevGap || this.bandGap * 20))),
    );
    this.bandColors.push(
      new FormControl(
        DEFAULT_SERIES_COLORS[
          this.bandColors.length % DEFAULT_SERIES_COLORS.length
        ],
      ),
    );
  }

  removeBand(index: number): void {
    // band `index` sits between edge[index] and edge[index+1]
    this.bandEdges.removeAt(index + 1);
    this.bandColors.removeAt(index);
  }

  // ----- validation (ported from prototype validate()) --------------------

  private validate(): string[] {
    const errs: string[] = [];
    const v = this.form.getRawValue();
    const meta = this.meta;

    if (!v.categoryColumnKey) {
      errs.push(
        this.needsYAxis
          ? $localize`Select an X-axis column.`
          : $localize`Select a category / X-axis column.`,
      );
    }
    if (!v.name?.trim()) {
      errs.push($localize`Give the chart a name.`);
    }
    if (!v.renderTag?.trim()) {
      errs.push(
        $localize`Set a Pdf tag (the placeholder token used in the docx template).`,
      );
    }
    if (
      v.renderTag?.trim() &&
      (this.data.usedRenderTags ?? []).includes(v.renderTag.trim())
    ) {
      errs.push(
        $localize`Another chart in this template already uses this Pdf tag.`,
      );
    }

    const validSeries = (v.series ?? []).filter(
      (s: { valueColumnKey?: string }) => s.valueColumnKey,
    );

    if (meta.needsYAxis) {
      if (!v.yAxisColumnKey) {
        errs.push($localize`Select a Y-axis column.`);
      }
      if (v.yAxisColumnKey && v.yAxisColumnKey === v.categoryColumnKey) {
        errs.push($localize`X-axis and Y-axis must be different columns.`);
      }
      const xc = this.columns.find((c) => c.columnKey === v.categoryColumnKey);
      const yc = this.columns.find((c) => c.columnKey === v.yAxisColumnKey);
      if (xc && !isNumericAxisColumn(xc)) {
        errs.push(
          $localize`The X-axis column must be numeric, or a dropdown with numeric values assigned to every option.`,
        );
      }
      if (yc && !isNumericAxisColumn(yc)) {
        errs.push(
          $localize`The Y-axis column must be numeric, or a dropdown with numeric values assigned to every option.`,
        );
      }
      if (validSeries.length !== 1) {
        errs.push(
          $localize`This chart type needs exactly one value column (the Z axis).`,
        );
      }
      const edges = (v.bandEdges ?? []) as number[];
      if (edges.length < 2) {
        errs.push($localize`Add at least one band.`);
      }
      for (let i = 1; i < edges.length; i++) {
        if (!(edges[i] > edges[i - 1])) {
          errs.push($localize`Band edges must strictly increase.`);
          break;
        }
      }
      if ((v.bandColors ?? []).length !== edges.length - 1) {
        errs.push(
          $localize`Number of band colors must equal number of bands (edges − 1).`,
        );
      }
    } else {
      if (validSeries.length < 1) {
        errs.push($localize`Add at least one value series.`);
      }
      if (this.chartType === "PIE" && validSeries.length > 1) {
        errs.push($localize`Pie charts support exactly one value series.`);
      }
      const keys = validSeries.map(
        (s: { valueColumnKey: string }) => s.valueColumnKey,
      );
      if (new Set(keys).size !== keys.length) {
        errs.push(
          $localize`Two series reference the same column — pick distinct columns.`,
        );
      }
    }

    (v.series ?? []).forEach((s: { valueColumnKey?: string }) => {
      if (
        s.valueColumnKey &&
        (s.valueColumnKey === v.categoryColumnKey ||
          s.valueColumnKey === v.yAxisColumnKey)
      ) {
        errs.push($localize`A value series can't reuse an axis column.`);
      }
    });

    return Array.from(new Set(errs));
  }

  // ----- preview ---------------------------------------------------------

  private previewConfig(): ChartPreviewConfig {
    const v = this.form.getRawValue();
    return {
      chartType: v.chartType,
      categoryColumnKey: v.categoryColumnKey,
      yAxisColumnKey: v.yAxisColumnKey,
      aggregation: this.needsBands ? "NONE" : v.aggregation,
      series: (v.series ?? []).filter(
        (s: { valueColumnKey?: string }) => s.valueColumnKey,
      ),
      bandEdges: (v.bandEdges ?? []).map(Number),
      bandColors: v.bandColors ?? [],
      showLegend: v.showLegend,
      title: v.title,
      name: v.name,
      xAxisLabel: v.xAxisLabel,
      yAxisLabel: v.yAxisLabel,
      zAxisLabel: v.zAxisLabel,
      widthPx: Math.max(300, Math.min(1000, +v.widthPx || 600)),
      heightPx: Math.max(220, Math.min(800, +v.heightPx || 350)),
      viewAzimuth: +this.viewAzimuth,
      viewElevation: +this.viewElevation,
      surfaceOpacity: +this.surfaceOpacity,
      smoothSurface: this.smoothSurface,
    };
  }

  /** re-draw the preview from existing data (view-angle / opacity tweaks) */
  redraw(): void {
    this.renderPreview();
  }

  /** signature of the inputs that change the *shape* of the generated data */
  private structuralKey(): string {
    const v = this.form.getRawValue();
    return JSON.stringify({
      t: v.chartType,
      c: v.categoryColumnKey,
      y: v.yAxisColumnKey,
      s: (v.series ?? []).map((s: { valueColumnKey?: string }) => s.valueColumnKey),
      n: +this.rowCount,
      lo: +this.valueMin,
      hi: +this.valueMax,
    });
  }

  get valueRange(): { min: number; max: number } {
    return { min: +this.valueMin, max: +this.valueMax };
  }

  matchValueRangeToBands(): void {
    const edges = (this.bandEdges.value as number[]).map(Number);
    if (edges.length >= 2) {
      this.valueMin = edges[0];
      this.valueMax = edges[edges.length - 1];
      this.regenerateIfStale();
    }
  }

  onRangeChange(): void {
    this.regenerateIfStale();
  }

  private regenerateIfStale(): void {
    if (this.previewData && this.structuralKey() !== this.generatedKey) {
      this.previewData = null;
      this.previewSvg = null;
    }
  }

  generatePreview(): void {
    if (this.errors.length > 0) {
      this.previewData = null;
      this.previewSvg = null;
      return;
    }
    this.previewData = generatePreviewData(
      this.previewConfig(),
      this.columns,
      this.rowCount,
      this.needsYAxis,
      this.valueRange,
    );
    this.generatedKey = this.structuralKey();
    this.renderPreview();
  }

  /** re-draw the SVG from the current (possibly hand-edited) preview data */
  private renderPreview(): void {
    if (!this.previewData) {
      this.previewSvg = null;
      return;
    }
    const svg = renderChartSvg(this.previewConfig(), this.previewData);
    this.previewSvg = svg
      ? this.sanitizer.bypassSecurityTrustHtml(svg)
      : null;
  }

  private recompute(): void {
    this.errors = this.validate();
    if (this.errors.length > 0) {
      this.previewSvg = null;
      return;
    }
    // config changed the data shape → force a fresh "Generate"
    if (this.previewData && this.structuralKey() !== this.generatedKey) {
      this.previewData = null;
      this.previewSvg = null;
      return;
    }
    // label / band / size tweaks → live re-render from existing data
    if (this.previewData) {
      this.renderPreview();
    }
  }

  // ----- editable preview data -----------------------------------------

  get previewRows(): Extract<PreviewData, { kind: "rows" }> | null {
    return this.previewData && this.previewData.kind === "rows"
      ? this.previewData
      : null;
  }
  get previewGrid(): Extract<PreviewData, { kind: "grid" }> | null {
    return this.previewData && this.previewData.kind === "grid"
      ? this.previewData
      : null;
  }

  fmt(v: unknown): string {
    if (typeof v === "number") {
      return Number.isInteger(v) ? String(v) : String(Math.round(v * 1000) / 1000);
    }
    return String(v ?? "");
  }

  rowCell(
    row: { [key: string]: string | number },
    key: string,
  ): string | number {
    return row[key];
  }

  setRowCategory(index: number, value: string): void {
    const rows = this.previewRows;
    if (!rows) {
      return;
    }
    rows.rows[index].category = value;
    this.renderPreview();
  }

  setRowValue(index: number, key: string, value: string): void {
    const rows = this.previewRows;
    if (!rows) {
      return;
    }
    rows.rows[index][key] = value === "" ? 0 : Number(value);
    this.renderPreview();
  }

  setGridZ(index: number, value: string): void {
    const grid = this.previewGrid;
    if (!grid) {
      return;
    }
    grid.points[index].z = value === "" ? 0 : Number(value);
    this.renderPreview();
  }

  // ----- commit --------------------------------------------------------

  get canSave(): boolean {
    return this.errors.length === 0;
  }

  save(): void {
    if (!this.canSave) {
      return;
    }
    const v = this.form.getRawValue();
    const meta = this.meta;

    const cleanSeries = (v.series ?? [])
      .filter((s: { valueColumnKey?: string }) => s.valueColumnKey)
      .map(
        (
          s: { valueColumnKey: string; label?: string; color?: string },
          i: number,
        ) => ({
          valueColumnKey: s.valueColumnKey,
          label: s.label || null,
          color: meta.needsBands ? null : s.color,
          sort: i,
        }),
      );

    const chart = new ServiceChart({
      name: v.name?.trim(),
      title: v.title?.trim() || null,
      renderTag: v.renderTag?.trim(),
      chartType: v.chartType,
      categoryColumnKey: v.categoryColumnKey,
      yAxisColumnKey: meta.needsYAxis ? v.yAxisColumnKey : null,
      aggregation: meta.needsBands ? "NONE" : v.aggregation ?? "NONE",
      xAxisLabel: v.xAxisLabel?.trim() || null,
      yAxisLabel: v.yAxisLabel?.trim() || null,
      zAxisLabel: meta.needsYAxis ? v.zAxisLabel?.trim() || null : null,
      showLegend: !!v.showLegend,
      showDataLabels: !!v.showDataLabels,
      attachDataTable: !!v.attachDataTable,
      widthPx: Math.max(300, Math.min(1000, +v.widthPx || 600)),
      heightPx: Math.max(220, Math.min(800, +v.heightPx || 350)),
      bandEdges: meta.needsBands ? (v.bandEdges ?? []).map(Number) : null,
      bandColors: meta.needsBands ? v.bandColors ?? [] : null,
      viewAzimuth:
        v.chartType === "SURFACE_3D"
          ? Math.round(+this.viewAzimuth) || 0
          : null,
      viewElevation:
        v.chartType === "SURFACE_3D"
          ? Math.round(+this.viewElevation) || 0
          : null,
      surfaceOpacity: meta.needsBands
        ? Math.round(Math.max(0.15, Math.min(1, +this.surfaceOpacity || 1)) * 100) /
          100
        : null,
      smoothSurface: meta.needsBands ? !!this.smoothSurface : null,
      sort: +v.sort || 0,
      series: meta.needsBands ? cleanSeries.slice(0, 1) : cleanSeries,
    });

    this.dialogRef.close(chart);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
